import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  Lock,
  Unlock,
  BarChart3,
  Users,
  ArrowRight,
  Brain,
  TrendingUp,
  ShieldCheck,
  Heart,
  HandHeart,
  Shield,
  Database,
  Layers,
  Plug,
  ClipboardList,
  Sparkles,
} from 'lucide-react';
import { UdvegaIcon } from '../components/UdvegaLogo';
import DemoVideoPlayer from '../components/DemoVideoPlayer';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const AVATAR_COLORS = ['#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4'];

/** People nodes in the culture network (viewBox 0–400) */
const PEOPLE_NODES = [
  { cx: 200, cy: 200, r: 22, label: 'You', core: true },
  { cx: 95, cy: 120, r: 14, color: '#0D9488', delay: 0 },
  { cx: 305, cy: 110, r: 13, color: '#14B8A6', delay: 0.4 },
  { cx: 320, cy: 250, r: 12, color: '#2DD4BF', delay: 0.8 },
  { cx: 80, cy: 260, r: 13, color: '#5EEAD4', delay: 1.2 },
  { cx: 200, cy: 70, r: 11, color: '#99F6E4', delay: 1.6 },
  { cx: 200, cy: 330, r: 12, color: '#14B8A6', delay: 2 },
];

const CULTURE_EDGES = [
  [0, 1],
  [0, 2],
  [0, 3],
  [0, 4],
  [0, 5],
  [0, 6],
  [1, 5],
  [2, 5],
  [3, 6],
  [4, 6],
];

const CULTURE_PILLARS = [
  {
    title: 'Psychological Safety',
    line: 'Speak freely. Grow together.',
    icon: HandHeart,
    x: '2%',
    y: '4%',
    delay: 0.15,
  },
  {
    title: 'Anonymous Voice',
    line: 'Identity sealed. Insight clear.',
    icon: Lock,
    x: '64%',
    y: '2%',
    delay: 0.3,
  },
  {
    title: 'Team Trust',
    line: 'Patterns for HR — never persons.',
    icon: ShieldCheck,
    x: '66%',
    y: '68%',
    delay: 0.45,
  },
  {
    title: 'People First',
    line: 'Culture cared for before burnout.',
    icon: Heart,
    x: '0%',
    y: '70%',
    delay: 0.6,
  },
];

// Minimal grayscale "startup" wordmarks for the social-proof strip
const TRUST_LOGOS = [
  { name: 'Northwind', shape: 'circle' },
  { name: 'Lumen', shape: 'triangle' },
  { name: 'Quanta', shape: 'square' },
  { name: 'Vertex', shape: 'diamond' },
  { name: 'Nimbus', shape: 'hex' },
];

const TRUST_PILLARS = [
  {
    icon: Shield,
    title: '100% Anonymity Firewall',
    desc: 'Employees give honest, unfiltered feedback without fear. Identities stay sealed — so CHROs get the truth, not the polite version.',
  },
  {
    icon: Database,
    title: 'Enterprise Data Encryption',
    desc: 'Survey data is encrypted in transit and at rest. Built for the security bar People Operations and InfoSec already expect.',
  },
  {
    icon: Layers,
    title: 'Aggregated Insights Only',
    desc: 'No individual employee tracking. Dashboards show team-level patterns only — never a named person, never a private score.',
  },
];

const WORKFLOW_STEPS = [
  {
    step: '01',
    icon: Plug,
    title: 'Quick Setup',
    desc: 'Stand up Udvega in minutes and drop it beside your existing HR tech stack — no rip-and-replace, no six-month rollout.',
  },
  {
    step: '02',
    icon: ClipboardList,
    title: 'Automated Pulse Checks',
    desc: 'Smart, frictionless micro-surveys go out to employees as an anonymous pulse feedback tool they actually finish.',
  },
  {
    step: '03',
    icon: Sparkles,
    title: 'AI Burnout Alerts & Action Playbooks',
    desc: 'Get predictive risk signals and practical management strategies before resignations happen — not after the exit interview.',
  },
];

function SlackMark() {
  return (
    <svg viewBox="0 0 24 24" className="w-8 h-8" aria-hidden>
      <path fill="#E01E5A" d="M5.04 15.2a2.04 2.04 0 1 1-2.04-2.04h2.04v2.04Z" />
      <path fill="#E01E5A" d="M6.06 15.2A2.04 2.04 0 0 1 10.14 15.2V8.08a2.04 2.04 0 1 0-4.08 0v7.12Z" />
      <path fill="#36C5F0" d="M8.8 5.04a2.04 2.04 0 1 1 2.04-2.04v2.04H8.8Z" />
      <path fill="#36C5F0" d="M8.8 6.06A2.04 2.04 0 0 1 8.8 10.14H15.92a2.04 2.04 0 1 0 0-4.08H8.8Z" />
      <path fill="#2EB67D" d="M18.96 8.8a2.04 2.04 0 1 1 2.04 2.04h-2.04V8.8Z" />
      <path fill="#2EB67D" d="M17.94 8.8A2.04 2.04 0 0 1 13.86 8.8v7.12a2.04 2.04 0 1 0 4.08 0V8.8Z" />
      <path fill="#ECB22E" d="M15.2 18.96a2.04 2.04 0 1 1-2.04 2.04v-2.04h2.04Z" />
      <path fill="#ECB22E" d="M15.2 17.94A2.04 2.04 0 0 1 15.2 13.86H8.08a2.04 2.04 0 1 0 0 4.08H15.2Z" />
    </svg>
  );
}

function TeamsMark() {
  return (
    <svg viewBox="0 0 24 24" className="w-8 h-8" aria-hidden>
      <rect x="3" y="6" width="12" height="12" rx="2.2" fill="#5059C9" />
      <text x="9" y="15" textAnchor="middle" fill="white" fontSize="7.5" fontFamily="Segoe UI, Arial, sans-serif" fontWeight="700">
        T
      </text>
      <circle cx="17.2" cy="8.2" r="2.15" fill="#7B83EB" />
      <rect x="14.6" y="11.2" width="6.4" height="6.6" rx="2.2" fill="#7B83EB" />
    </svg>
  );
}

function LogoMark({ shape }) {
  const common = 'fill-current';
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" className={common} aria-hidden>
      {shape === 'circle' && <circle cx="10" cy="10" r="7" />}
      {shape === 'triangle' && <path d="M10 3 L17 16 L3 16 Z" />}
      {shape === 'square' && <rect x="4" y="4" width="12" height="12" rx="2" />}
      {shape === 'diamond' && <path d="M10 2 L18 10 L10 18 L2 10 Z" />}
      {shape === 'hex' && <path d="M6 3 H14 L18 10 L14 17 H6 L2 10 Z" />}
    </svg>
  );
}

function Donut({ value = 82 }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative w-[90px] h-[90px] shrink-0">
      <svg width="90" height="90" className="-rotate-90">
        <defs>
          <filter id="donutGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle cx="45" cy="45" r={r} stroke="#334155" strokeWidth="8" fill="none" />
        <circle
          cx="45"
          cy="45"
          r={r}
          stroke="#2DD4BF"
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          filter="url(#donutGlow)"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-heading text-xl font-semibold text-white">{value}%</span>
      </div>
    </div>
  );
}

/** World-class glassmorphic culture scene — employees, HR trust, company care. */
function CultureConstellation() {
  return (
    <div
      className="relative w-full max-w-[440px] aspect-square mx-auto select-none"
      data-testid="hero-culture-visual"
      aria-hidden
    >
      {/* Ambient teal atmosphere */}
      <motion.div
        className="absolute inset-[4%] rounded-full pointer-events-none"
        animate={{ opacity: [0.45, 0.7, 0.45], scale: [1, 1.04, 1] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          background:
            'radial-gradient(circle at 50% 45%, rgba(13,148,136,0.5) 0%, rgba(20,184,166,0.18) 38%, transparent 68%)',
          filter: 'blur(28px)',
        }}
      />
      <div
        className="absolute inset-[18%] rounded-full pointer-events-none opacity-40"
        style={{
          background:
            'radial-gradient(circle, rgba(45,212,191,0.25) 0%, transparent 55%)',
          filter: 'blur(16px)',
        }}
      />

      {/* Frosted glass disc behind the network */}
      <div className="absolute inset-[10%] rounded-full border border-white/[0.08] bg-white/[0.03] backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]" />

      {/* Orbit rings */}
      <motion.div
        className="absolute inset-[8%] rounded-full border border-teal-400/25"
        animate={{ rotate: 360 }}
        transition={{ duration: 42, repeat: Infinity, ease: 'linear' }}
      >
        <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-teal-300 shadow-[0_0_16px_rgba(45,212,191,0.9)]" />
        <span className="absolute bottom-[12%] left-[10%] w-1.5 h-1.5 rounded-full bg-emerald-300/90" />
      </motion.div>
      <motion.div
        className="absolute inset-[20%] rounded-full border border-dashed border-teal-500/20"
        animate={{ rotate: -360 }}
        transition={{ duration: 58, repeat: Infinity, ease: 'linear' }}
      >
        <span className="absolute top-[20%] right-0 w-1.5 h-1.5 rounded-full bg-teal-400/70" />
      </motion.div>

      {/* Living culture network */}
      <svg
        className="absolute inset-0 w-full h-full z-[5]"
        viewBox="0 0 400 400"
        fill="none"
      >
        <defs>
          <linearGradient id="cultureEdge" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0D9488" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#2DD4BF" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#0D9488" stopOpacity="0.15" />
          </linearGradient>
          <filter id="nodeGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {CULTURE_EDGES.map(([a, b], i) => {
          const from = PEOPLE_NODES[a];
          const to = PEOPLE_NODES[b];
          return (
            <motion.line
              key={`e-${i}`}
              x1={from.cx}
              y1={from.cy}
              x2={to.cx}
              y2={to.cy}
              stroke="url(#cultureEdge)"
              strokeWidth="1.25"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.3, 0.7, 0.3] }}
              transition={{
                opacity: { delay: 0.4 + i * 0.05, duration: 3.2 + i * 0.15, repeat: Infinity, ease: 'easeInOut' },
              }}
            />
          );
        })}

        {/* Pulse traveling on main spokes */}
        {[1, 2, 3, 4].map((idx) => {
          const node = PEOPLE_NODES[idx];
          return (
            <motion.circle
              key={`pulse-${idx}`}
              r="2.5"
              fill="#5EEAD4"
              filter="url(#nodeGlow)"
              initial={{ cx: 200, cy: 200, opacity: 0 }}
              animate={{
                cx: [200, node.cx, 200],
                cy: [200, node.cy, 200],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3.2,
                delay: idx * 0.7,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          );
        })}

        {PEOPLE_NODES.filter((n) => !n.core).map((node, i) => (
          <motion.g
            key={`n-${i}`}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 + i * 0.08, duration: 0.5 }}
          >
            <motion.circle
              cx={node.cx}
              cy={node.cy}
              r={node.r + 6}
              fill={node.color}
              fillOpacity="0.12"
              animate={{ r: [node.r + 4, node.r + 10, node.r + 4] }}
              transition={{
                duration: 3.5,
                delay: node.delay,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            <circle
              cx={node.cx}
              cy={node.cy}
              r={node.r}
              fill={node.color}
              filter="url(#nodeGlow)"
              opacity="0.95"
            />
            <circle
              cx={node.cx - node.r * 0.28}
              cy={node.cy - node.r * 0.28}
              r={node.r * 0.28}
              fill="white"
              fillOpacity="0.35"
            />
          </motion.g>
        ))}
      </svg>

      {/* Floating culture glass cards */}
      {CULTURE_PILLARS.map((pillar) => {
        const Icon = pillar.icon;
        return (
          <motion.div
            key={pillar.title}
            className="absolute z-20 w-[142px] sm:w-[152px]"
            style={{ left: pillar.x, top: pillar.y }}
            initial={{ opacity: 0, y: 14, scale: 0.96 }}
            animate={{ opacity: 1, y: [0, -8, 0], scale: 1 }}
            transition={{
              opacity: { delay: pillar.delay, duration: 0.55 },
              scale: { delay: pillar.delay, duration: 0.55 },
              y: {
                delay: pillar.delay + 0.5,
                duration: 5 + pillar.delay,
                repeat: Infinity,
                ease: 'easeInOut',
              },
            }}
          >
            <div
              className="relative overflow-hidden rounded-2xl border border-white/15 p-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.75)] hover:border-teal-400/40 transition-colors duration-300"
              style={{
                background:
                  'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(15,23,42,0.55) 45%, rgba(13,148,136,0.12) 100%)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
              <div className="w-8 h-8 rounded-xl bg-teal-500/20 border border-teal-400/30 flex items-center justify-center mb-2 shadow-[0_0_16px_rgba(13,148,136,0.25)]">
                <Icon className="w-4 h-4 text-teal-300" />
              </div>
              <p className="text-[11px] font-semibold text-white leading-tight tracking-tight">
                {pillar.title}
              </p>
              <p className="text-[10px] text-slate-300/80 mt-1 leading-snug">{pillar.line}</p>
            </div>
          </motion.div>
        );
      })}

      {/* Center glass orb — culture heart */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
        initial={{ opacity: 0, scale: 0.88 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15, duration: 0.7 }}
      >
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
          className="relative w-[158px] h-[158px] sm:w-[172px] sm:h-[172px]"
        >
          {/* Breathing ring */}
          <motion.div
            className="absolute -inset-3 rounded-full border border-teal-400/30"
            animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.15, 0.5] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div
            className="absolute inset-0 rounded-full border border-white/20 shadow-[0_0_60px_-8px_rgba(13,148,136,0.65)] flex flex-col items-center justify-center text-center px-4 overflow-hidden"
            style={{
              background:
                'linear-gradient(160deg, rgba(255,255,255,0.14) 0%, rgba(15,23,42,0.65) 40%, rgba(13,148,136,0.22) 100%)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
            }}
          >
            <div className="absolute inset-x-4 top-3 h-8 rounded-full bg-white/10 blur-xl" />
            <div className="absolute inset-4 rounded-full border border-teal-400/15" />
            <motion.div
              className="relative w-11 h-11 rounded-2xl gradient-teal flex items-center justify-center mb-2 shadow-[0_0_28px_rgba(13,148,136,0.55)]"
              animate={{ rotate: [0, 4, -4, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Users className="w-5 h-5 text-white" />
            </motion.div>
            <p className="relative font-heading text-sm font-semibold text-white tracking-tight">
              Culture Alive
            </p>
            <p className="relative text-[10px] text-teal-200/90 mt-0.5">People · HR · Belonging</p>
            <div className="relative flex -space-x-2 mt-3">
              {AVATAR_COLORS.map((c, i) => (
                <motion.div
                  key={i}
                  className="w-6 h-6 rounded-full border-2 border-slate-900/70"
                  style={{ background: c }}
                  animate={{ y: [0, -2, 0] }}
                  transition={{
                    duration: 2.4,
                    delay: i * 0.15,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero — brand teal, culture-first visual */}
      <section
        className="relative pt-28 pb-24 md:pt-32 md:pb-28 overflow-hidden"
        style={{ backgroundColor: '#080C14' }}
        data-testid="hero-section"
      >
        {/* Dot matrix overlay */}
        <div
          className="absolute inset-0 opacity-[0.28] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(148,163,184,0.3) 0.8px, transparent 0.8px)',
            backgroundSize: '22px 22px',
            maskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 75%)',
          }}
        />
        {/* Teal brand atmosphere — no blue */}
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 w-[55%] h-[80%] max-w-2xl pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(13,148,136,0.32) 0%, rgba(16,185,129,0.12) 40%, transparent 70%)',
          }}
        />
        <div
          className="absolute left-[-5%] bottom-[-10%] w-[40%] h-[50%] pointer-events-none opacity-50"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(15,118,110,0.3) 0%, transparent 65%)',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 xl:px-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left copy */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="lg:col-span-7 min-w-0 pr-0 lg:pr-4 order-1"
            >
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-600/20 border border-teal-500/30 mb-6"
              >
                <UdvegaIcon size={16} />
                <span className="text-xs font-medium text-teal-300 tracking-wide">
                  AI-Driven Workforce Intelligence
                </span>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight font-semibold text-white leading-[1.08] mb-5"
              >
                Predict. Prevent. Preserve.
                <span className="block mt-2 text-3xl sm:text-4xl md:text-5xl font-semibold text-teal-400">
                  The Future of Workforce Resilience.
                </span>
              </motion.h1>

              <motion.p variants={fadeUp} className="text-base md:text-lg text-slate-400 leading-relaxed mb-8 max-w-xl">
                Udvega turns anonymous employee feedback into predictive burnout intelligence — so you can strengthen workplace culture, boost employee retention, and protect psychological safety before your best people quietly disappear.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-3.5">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-teal-600 hover:bg-teal-700 text-white px-8 h-12 text-base hover:shadow-[0_0_24px_rgba(13,148,136,0.45)] transition-shadow"
                    data-testid="hero-demo-btn"
                  >
                    Book a Demo <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:border-teal-500/40 px-8 h-12 text-base"
                    data-testid="hero-privacy-btn"
                  >
                    How We Protect Privacy
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right — living culture glass scene */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="flex lg:col-span-5 items-center justify-center lg:justify-end order-2 mt-4 lg:mt-0"
            >
              <CultureConstellation />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Transition band (dark -> light) + Social Proof Trust Strip */}
      <section className="bg-gradient-to-b from-[#080C14] to-[#F8FAFC] pt-16 pb-14" data-testid="trust-strip">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400 mb-8"
          >
            Trusted by Forward-Thinking HR &amp; People Teams
          </motion.p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {TRUST_LOGOS.map((logo) => (
              <div
                key={logo.name}
                className="flex items-center gap-2 text-slate-500 grayscale opacity-50 hover:opacity-100 transition-opacity duration-300"
              >
                <LogoMark shape={logo.shape} />
                <span className="font-heading text-lg font-semibold tracking-tight">{logo.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Bar - soft light slate, floating cards */}
      <section className="bg-[#F8FAFC]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center"
          >
            {[
              { value: '95%+', label: 'Prediction Accuracy', icon: Brain },
              { value: '100%', label: 'Anonymity Guaranteed', icon: Lock },
              { value: '36', label: 'Psychological Parameters', icon: BarChart3 },
            ].map((stat) => (
              <motion.div
                key={stat.label}
                variants={fadeUp}
                className="flex flex-col items-center gap-2 p-6 rounded-2xl bg-white shadow-xl shadow-slate-300/30 hover:shadow-2xl hover:shadow-teal-500/10 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center mb-1">
                  <stat.icon className="w-5 h-5 text-teal-600" />
                </div>
                <span className="font-heading text-3xl font-semibold text-slate-800">{stat.value}</span>
                <span className="text-sm text-slate-500">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Product demo — text + video, not the hero */}
      <section className="bg-[#F8FAFC] pb-8 md:pb-12" data-testid="product-demo-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center rounded-3xl bg-white border border-slate-100 shadow-xl shadow-slate-300/20 p-6 md:p-10">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold tracking-[0.12em] uppercase text-teal-600 mb-3">
                Product demo
              </p>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-slate-800 mb-3">
                See the dashboard in 60 seconds
              </h2>
              <p className="text-sm md:text-base text-slate-500 leading-relaxed">
                A short walkthrough of anonymous pulse insights, team burnout risk, and the action playbook CHROs use — without a sales call.
              </p>
            </div>
            <div className="lg:col-span-7">
              <DemoVideoPlayer />
            </div>
          </div>
        </div>
      </section>

      {/* Anonymity Engine */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="anonymity-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600 mb-3">The Anonymity Engine</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl tracking-tight font-medium text-slate-800 mb-4">
              Predictive Insights, Zero Compromise
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-500 max-w-2xl mx-auto mb-10">
              HR gets team-level burnout insight. Employees stay anonymous — honest feedback without fear.
            </motion.p>

            {/* Anonymity Firewall visual widget */}
            <motion.div variants={fadeUp} className="max-w-2xl mx-auto" data-testid="anonymity-firewall-widget">
              <div className="rounded-2xl bg-white shadow-xl shadow-slate-300/30 border border-slate-100 p-5 md:p-6">
                <div className="flex items-center justify-center gap-2 mb-5">
                  <ShieldCheck className="w-4 h-4 text-teal-600" />
                  <span className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Anonymity Firewall</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Publicly visible team */}
                  <div className="rounded-xl border border-green-100 bg-green-50/60 p-4 text-left">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Sales Team</p>
                        <p className="text-xs text-slate-500">15 Responses</p>
                      </div>
                      <div className="flex -space-x-1.5">
                        {['#0D9488', '#6366F1', '#F59E0B'].map((c, i) => (
                          <div key={i} className="w-6 h-6 rounded-full border-2 border-white" style={{ background: c }} />
                        ))}
                        <div className="w-6 h-6 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[8px] font-semibold text-slate-500">+12</div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-100 rounded-full px-2.5 py-1">
                      <Unlock className="w-3.5 h-3.5" /> Publicly Visible
                    </span>
                  </div>

                  {/* Hidden team */}
                  <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4 text-left">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">HR Team</p>
                        <p className="text-xs text-slate-500">1 Response</p>
                      </div>
                      <div className="flex -space-x-1.5">
                        <div className="w-6 h-6 rounded-full border-2 border-white bg-slate-300" />
                        <div className="w-6 h-6 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center">
                          <Lock className="w-3 h-3 text-slate-400" />
                        </div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full px-2.5 py-1">
                      <Lock className="w-3.5 h-3.5" /> Encrypted &amp; Hidden
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed mt-5 text-center">
                  <span className="font-semibold text-slate-500">Anonymity Safeguard:</span> Analytics automatically lock for teams with fewer than 2 responses to prevent contextual identity guessing.
                </p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
          >
            {TRUST_PILLARS.map((item) => (
              <motion.div
                key={item.title}
                variants={fadeUp}
                className="bg-white rounded-2xl p-8 shadow-xl shadow-slate-300/30 hover:shadow-2xl hover:shadow-teal-500/10 hover:-translate-y-1.5 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-800 mb-3">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-white to-[#F8FAFC]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600 mb-3">How It Works</motion.p>
              <motion.h2 variants={fadeUp} className="font-heading text-4xl tracking-tight font-medium text-slate-800 mb-6">
                From Data to Action, in Minutes
              </motion.h2>
              <motion.div variants={fadeUp} className="space-y-6">
                {WORKFLOW_STEPS.map((item, i) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-8 h-8 shrink-0 rounded-full gradient-teal flex items-center justify-center text-white text-sm font-medium">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-heading font-medium text-slate-800 mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="rounded-3xl p-6 bg-white shadow-2xl shadow-slate-400/20"
            >
              <div className="bg-slate-900 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="text-xs text-slate-400 ml-2">Udvega Dashboard</span>
                </div>
                <div className="space-y-4">
                  {/* Team Health Index — Donut */}
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-800">
                    <Donut value={82} />
                    <div>
                      <p className="text-sm text-slate-300">Team Health Index</p>
                      <p className="text-xs text-slate-500 mt-0.5">Aggregated across all teams</p>
                      <p className="text-teal-400 text-xs font-medium mt-1.5 flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5" /> +6% vs last review
                      </p>
                    </div>
                  </div>

                  {/* Burnout Risk — glowing amber dot */}
                  <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800">
                    <span className="text-sm text-slate-300">Burnout Risk</span>
                    <span className="flex items-center gap-2 text-amber-400 font-semibold">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-70" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400 shadow-[0_0_8px_2px_rgba(251,191,36,0.6)]" />
                      </span>
                      Medium
                    </span>
                  </div>

                  {/* Top Driver */}
                  <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800">
                    <span className="text-sm text-slate-300">Top Driver</span>
                    <span className="text-red-400 font-semibold">Incivility</span>
                  </div>

                  {/* Active Assessments — avatar stack */}
                  <div className="flex justify-between items-center p-3 rounded-xl bg-slate-800">
                    <span className="text-sm text-slate-300">Active Assessments</span>
                    <div className="flex items-center gap-3">
                      <div className="flex -space-x-2">
                        {AVATAR_COLORS.map((c, i) => (
                          <div
                            key={i}
                            className="w-6 h-6 rounded-full border-2 border-slate-800"
                            style={{ background: c }}
                          />
                        ))}
                        <div className="w-6 h-6 rounded-full border-2 border-slate-800 bg-slate-700 flex items-center justify-center text-[9px] font-semibold text-slate-300">
                          +176
                        </div>
                      </div>
                      <span className="text-teal-400 font-semibold">181</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrations roadmap */}
      <section className="py-16 md:py-20 bg-white" data-testid="integrations-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-teal-600 mb-3 text-center">Coming soon</p>
          <h2 className="font-heading text-3xl tracking-tight font-medium text-slate-800 mb-8 text-center">
            Slack &amp; Microsoft Teams
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { name: 'Slack', mark: SlackMark },
              { name: 'Microsoft Teams', mark: TeamsMark },
            ].map((item) => (
              <div
                key={item.name}
                className="relative flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5"
              >
                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
                  <item.mark />
                </div>
                <div>
                  <p className="font-heading font-medium text-slate-800">{item.name}</p>
                  <p className="text-xs font-semibold uppercase tracking-wide text-teal-700">Coming Soon</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-slate-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl tracking-tight font-medium text-white mb-4">
              Ready to Protect Your Workforce?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base text-slate-400 max-w-xl mx-auto mb-8">
              Join forward-thinking organizations using AI to build healthier, more resilient teams.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/contact">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white px-10 h-12 text-base" data-testid="cta-demo-btn">
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
